import { useCallback, useRef } from 'react'
import { useProductFetcher } from '../../../api/useProductFetcher/useProductFetcher'
import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import type {
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
} from '../../../types/types'

/**
 * Internal hook that fetches backend content for a specific article ID.
 * Used to detect PT key paragraphs in collapsed articles whose children
 * have not yet been loaded into frontend state.
 */
export const useCollapsedArticleChildFetcher = () => {
    const backendNodesRef = useRef<Partial<ContentItemViewNodesType>[]>([])

    const { getContentItemViewNodesWithApolloClient } = useProductFetcher({
        overrideContentItemNodes: false,
        callback: async (
            response:
                | GetProjectProductFamilyViewResponse
                | GetProjectNuggetContentViewResponse
        ) => {
            const nodes =
                (response as GetProjectProductFamilyViewResponse)
                    .getProjectProductFamilyView?.contentItemViewNodes ||
                (response as GetProjectNuggetContentViewResponse)
                    .getProjectNugget?.contentItemViewNodes ||
                []
            backendNodesRef.current = nodes
        },
    })

    const fetchChildrenForArticle = useCallback(
        async (
            articleId: string
        ): Promise<Partial<ContentItemViewNodesType>[]> => {
            try {
                backendNodesRef.current = []
                await getContentItemViewNodesWithApolloClient([articleId])
                return backendNodesRef.current
            } catch (error) {
                console.error(
                    '[useCollapsedArticleChildFetcher] Failed to fetch children for article from backend:',
                    error
                )
                return []
            }
        },
        [getContentItemViewNodesWithApolloClient]
    )

    return { fetchChildrenForArticle }
}
